import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, ConfirmDialog, Section, StateView, formatRelative, useAuth, useToast } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";

/** One token of one of the account's logins — GET /tenants/{id}/tokens (auth.TenantToken). */
interface AccountToken {
  id: number;
  name: string;
  owner_email: string;
  owner_role: string;
  scopes: string[] | null;
  allow_dangerous: boolean;
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
}

/** One recent revoke in the account (GET /tenants/{id}/tokens "revoked"). revoked_by is the account login
 *  that did it, or "the platform operator" — the server sends nothing more about an operator. */
interface RevokedToken {
  token_id: number;
  token_name: string;
  owner_email: string;
  cascaded: number;
  revoked_by: string;
  revoked_at: string;
}

interface TokenCalls {
  name: string;
  count: number;
  note?: string;
  calls: { module: string; action: string; ok: boolean; ip: string; node: string; at: string; ambiguous: boolean }[];
}

/**
 * Every API token of every login of one account (Oz, 2026-10-25): the account's admin and the
 * operator see — and can revoke — a token whichever login created it. The per-login
 * ApiTokensPanel stays the place to CREATE one; this is the place to find the one a colleague
 * made and forgot. One component for both consoles: the account's Security page and the admin's
 * account page.
 */
export function AccountApiTokens({ tenantId }: { tenantId: number | string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const key = ["account-api-tokens", String(tenantId)];
  const [showCalls, setShowCalls] = React.useState<number | null>(null);
  const [revoking, setRevoking] = React.useState<AccountToken | null>(null);
  const [selfAck, setSelfAck] = React.useState(false);
  const closeRevoke = () => {
    setRevoking(null);
    setSelfAck(false);
  };

  const tokens = useQuery({
    queryKey: key,
    queryFn: () => client.apiFetch<{ items: AccountToken[] | null; total: number; revoked: RevokedToken[] | null }>(`/tenants/${tenantId}/tokens`),
  });
  const calls = useQuery({
    queryKey: [...key, "calls", showCalls],
    enabled: showCalls != null,
    queryFn: () => client.apiFetch<TokenCalls>(`/tenants/${tenantId}/tokens/${showCalls}/calls?limit=50`),
  });
  // What a revoke takes with it — read before the dialog can confirm (GET …/cascade changes nothing).
  const cascade = useQuery({
    queryKey: [...key, "cascade", revoking?.id],
    enabled: revoking != null,
    queryFn: () =>
      client.apiFetch<{ id: number; descendant_ids: number[]; count: number; includes_current: boolean }>(
        `/tenants/${tenantId}/tokens/${revoking!.id}/cascade`,
      ),
  });
  const includesCurrent = !!cascade.data?.includes_current;
  const revoke = useMutation({
    mutationFn: (t: AccountToken) =>
      // expect = the count the dialog showed; a tree that changed since is refused (409), nothing revoked.
      client.apiFetch<{ cascaded: number }>(
        `/tenants/${tenantId}/tokens/${t.id}?confirm_self=${includesCurrent ? "true" : "false"}${cascade.data ? `&expect=${cascade.data.count}` : ""}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: (d, t) => {
      notify({
        kind: "success",
        title: `Token "${t.name}" revoked`,
        body: `It belonged to ${t.owner_email} and no longer authenticates.${d?.cascaded ? ` ${d.cascaded} token${d.cascaded === 1 ? "" : "s"} it created went with it.` : ""}`,
      });
      closeRevoke();
      if (showCalls === t.id) setShowCalls(null);
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["my-api-tokens"] });
    },
    onError: (e) => {
      notify({ kind: "error", title: "Could not revoke that token", body: e instanceof Error ? e.message : String(e) });
      void cascade.refetch();
    },
  });

  const items = tokens.data?.items ?? [];
  const revoked = tokens.data?.revoked ?? [];
  return (
    <Section title="API keys" hint="every key of every login of this account">
      <div className="p-4">
        <StateView loading={tokens.isLoading} error={tokens.error} errorContext="the account's API tokens">
          <>
            <div className="pb-2 text-[12px] leading-relaxed text-muted">
              Revoking deletes the token at once — its next call is refused. What it was used for stays in the audit trail.
            </div>
            {items.length === 0 && <div className="py-3 text-[13px] text-muted">No login of this account has an API token.</div>}
            <div className="space-y-2">
              {items.map((t) => (
                <div key={t.id} className="rounded border border-line px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[13px]">
                        <span className="font-medium">{t.name}</span>
                        <span className="font-mono text-[12px] text-muted">{t.owner_email}</span>
                        {t.allow_dangerous && <StatusPill tone="warn">dangerous</StatusPill>}
                        {!t.expires_at && <StatusPill tone="warn">never expires</StatusPill>}
                      </div>
                      <div className="text-[11.5px] text-muted">
                        {(t.scopes ?? []).join(", ") || "no scopes"}
                        {" · created "}
                        {formatRelative(t.created_at)}
                        {t.expires_at ? ` · expires ${formatRelative(t.expires_at)}` : ""}
                        {" · "}
                        {t.last_used_at ? `last used ${formatRelative(t.last_used_at)}` : "never used"}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" onClick={() => setShowCalls(showCalls === t.id ? null : t.id)}>
                        {showCalls === t.id ? "Hide" : "Usage"}
                      </Button>
                      <Button size="sm" variant="danger" disabled={revoke.isPending} onClick={() => setRevoking(t)}>
                        Revoke
                      </Button>
                    </div>
                  </div>
                  {showCalls === t.id && (
                    <div className="mt-2 border-t border-line pt-2">
                      {calls.isLoading && <div className="text-[12px] text-muted">Loading…</div>}
                      {calls.error && <div className="text-[12px] text-bad">The usage could not be read.</div>}
                      {calls.data?.note && <div className="mb-1 text-[11.5px] leading-relaxed text-warn">{calls.data.note}</div>}
                      {(calls.data?.calls ?? []).length === 0 && !calls.isLoading && !calls.error && (
                        <div className="text-[12px] text-muted">This token has not been used.</div>
                      )}
                      {(calls.data?.calls ?? []).map((c, i) => (
                        <div key={i} className="flex gap-2 text-[11.5px] leading-relaxed">
                          <span className="text-muted">{c.at.slice(0, 19).replace("T", " ")}</span>
                          <span className={c.ok ? "text-ok" : "text-bad"}>{c.ok ? "ok" : "failed"}</span>
                          <span className="truncate">{c.action}</span>
                          <span className="text-muted">{c.node}</span>
                          {c.ambiguous && <span className="text-warn">name-matched only</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {revoked.length > 0 && (
              <div className="mt-4 border-t border-line pt-3">
                <div className="pb-1 text-[12px] font-medium">Recently revoked</div>
                {revoked.map((x, i) => (
                  <div key={`${x.token_id}-${i}`} className="flex flex-wrap gap-x-2 text-[11.5px] leading-relaxed">
                    <span className="font-mono">{x.token_name || `#${x.token_id}`}</span>
                    {x.owner_email && <span className="text-muted">{x.owner_email}</span>}
                    <span>revoked by {x.revoked_by}</span>
                    <span className="text-muted">{formatRelative(x.revoked_at)}</span>
                    {x.cascaded > 0 && <span className="text-muted">with {x.cascaded} token{x.cascaded === 1 ? "" : "s"} it created</span>}
                  </div>
                ))}
              </div>
            )}
          </>
        </StateView>
      </div>
      <ConfirmDialog
        open={revoking != null}
        onOpenChange={(o: boolean) => !o && closeRevoke()}
        title={revoking ? `Revoke "${revoking.name}"?` : "Revoke token"}
        body={
          revoking ? (
            <div className="space-y-2">
              <div>
                This token belongs to <span className="font-mono">{revoking.owner_email}</span>. Anything using it stops
                working immediately, and it cannot be restored — a new token has to be created.
              </div>
              {cascade.isLoading && <div className="text-muted">Checking what this token created…</div>}
              {cascade.error ? <div className="text-bad">What this token created could not be read, so it cannot be revoked right now.</div> : null}
              {cascade.data && cascade.data.count > 0 && (
                <div className="rounded border border-warn/40 px-3 py-2 text-warn">
                  This also revokes {cascade.data.count} token{cascade.data.count === 1 ? "" : "s"} it created.
                </div>
              )}
              {includesCurrent && (
                <label className="flex items-start gap-2 rounded border border-bad/40 px-3 py-2 text-bad">
                  <input type="checkbox" checked={selfAck} onChange={(e) => setSelfAck(e.target.checked)} className="mt-0.5" />
                  <span>The token you are signed in with goes with it. Revoking it signs you out at once.</span>
                </label>
              )}
            </div>
          ) : null
        }
        confirmLabel="Revoke"
        destructive
        confirmDisabled={cascade.isLoading || !!cascade.error || (includesCurrent && !selfAck)}
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      />
    </Section>
  );
}
